import * as React from 'react';
import { ReactNode } from 'react';
import { UseInfiniteScrollHookResult } from 'react-infinite-scroll-hook';
import { FilterType } from '../data-table';
import { SortIcon, ChevronDownIcon, InfoIcon } from '../icons';
import {
  cn,
  DATE_TIME_BACKEND_FORMAT_NO_ZONE,
  getBackendTimestamp,
  getDayDateRange,
} from '../utils';
import { DateFilter } from './DateFilter';
import { DebouncedInput } from './DebouncedInput';
import { SelectFilter } from './SelectFilter';
import { TableTooltip } from './TableTooltip';
import { Tooltip } from './Tooltip';

const TableContainer = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'relative w-full rounded-md border shadow-[0_4px_6px_0_rgba(0,0,0,0.09)]',
      className
    )}
    {...props}
  />
));
TableContainer.displayName = 'TableContainer';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    wrapperProps?: React.HTMLAttributes<HTMLDivElement> &
      React.RefAttributes<HTMLDivElement>;
  }
>(({ className, wrapperProps, ...props }, ref) => (
  <div
    {...wrapperProps}
    className={cn('relative w-full overflow-auto', wrapperProps?.className)}
  >
    <table
      ref={ref}
      className={cn('w-full caption-bottom border-separate text-sm', className)}
      cellPadding={0}
      cellSpacing={0}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'bg-muted/50 border-t font-medium last:[&>tr]:border-b-0',
      '[&>tr]:bg-primary hover:[&>tr]:bg-primary [&>tr]:h-10 [&>tr>td]:h-10 [&>tr>td]:py-2 [&>tr>td]:font-semibold [&>tr>td]:text-white [&>tr>td:first-of-type]:rounded-bl-md [&>tr>td:last-of-type]:rounded-br-md',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'data-[state=selected]:bg-muted border-0 transition-colors hover:bg-[#EDF2F7]',
      '[&:last-of-type:not(:first-of-type)] group bg-white even:bg-[#F6F8FA]',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

export interface TableFilter {
  type: FilterType;
  value: string[];
  onChange: (values: string[]) => void;
  options?: { value: string; label: string }[];
  disabled?: boolean;
}

interface TableFilterProps {
  inputClassName?: string;
  selectClassName?: string;
  contentClassName?: string;
  disabledTooltip?: string;
  onSearchTermChange?: (searchTerm: string) => void;
  sentryRef?: UseInfiniteScrollHookResult['0'];
  hasNextPage?: boolean;
  isLoading?: boolean;
}

export interface TableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  canSort?: boolean;
  sortDirection?: 'asc' | 'desc';
  filter?: TableFilter;
  clearFilter?: () => void;
  setFilter?: (values: string[]) => void;
  settingsRow?: boolean;
  filterProps?: TableFilterProps;
  tooltip?: ReactNode;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  (
    {
      canSort = false,
      className,
      sortDirection,
      children,
      filter,
      clearFilter,
      setFilter,
      settingsRow,
      filterProps,
      tooltip,
      ...props
    },
    ref
  ) => (
    <th
      ref={ref}
      className={cn(
        'text-muted-foreground h-12 px-4 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
        'bg-midnight-500 border-border h-10 border-0 p-0 font-normal text-white first:rounded-tl-md last:rounded-tr-md',
        {
          'cursor-pointer select-none': canSort,
        },
        className
      )}
      {...props}
    >
      <div
        className={cn('box-border flex w-full flex-col gap-y-1 p-3', {
          'pb-0': settingsRow,
        })}
      >
        <div className="flex items-center gap-x-1.5">
          {tooltip ? (
            <TableTooltip tooltip={tooltip}>
              <span>{children}</span>
            </TableTooltip>
          ) : (
            children
          )}
          {canSort && (
            <span className="inline-block">
              {sortDirection ? (
                <ChevronDownIcon
                  className={cn('transition-transform', {
                    'rotate-180': sortDirection === 'asc',
                  })}
                />
              ) : (
                <SortIcon className="opacity-30" />
              )}
            </span>
          )}
          {tooltip && (
            <TableTooltip tooltip={tooltip}>
              <span className="ml-auto">
                <InfoIcon className="size-3.5" />
              </span>
            </TableTooltip>
          )}
        </div>
        {filter && (
          <TableFilters
            filter={filter}
            setFilter={setFilter}
            clearFilter={clearFilter}
            filterProps={filterProps}
          />
        )}
      </div>
    </th>
  )
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'border-0 p-4 align-middle [&:has([role=checkbox])]:pr-0',
      'text-muted-foreground h-[54px] border-b-[#E2E8F0] group-[&:last-of-type:not(:first-of-type)]:border-b',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

interface TableBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  colors?: string[];
}

const TableBadge = ({
  children,
  className,
  colors,
  ...props
}: TableBadgeProps) => (
  <span
    className={cn(
      'text-primary relative rounded bg-[#D2DFFD] px-2.5 py-2 whitespace-nowrap',
      className
    )}
    {...props}
  >
    {colors?.length ? (
      <div className="absolute top-0 left-0 h-full w-[5px]">
        {colors.map((color, index) => (
          <div
            key={index}
            className={cn(
              'h-full opacity-100 transition-opacity duration-300',
              {
                'rounded-t-md': index === 0,
                'rounded-b-md': index === colors.length - 1,
              }
            )}
            style={{
              backgroundColor: color,
              height: `${100 / colors.length}%`,
            }}
          />
        ))}
      </div>
    ) : null}

    {children}
  </span>
);

TableBadge.displayName = 'TableBadge';

interface TableFiltersProps {
  filter: TableFilter;
  setFilter?: (values: string[]) => void;
  clearFilter?: () => void;
  filterProps?: TableFilterProps;
  disabledTooltip?: string;
}

const FilterComponent = ({
  filter,
  setFilter,
  clearFilter,
  filterProps,
}: Omit<TableFiltersProps, 'disabledTooltip'>) => {
  if (filter?.type === FilterType.TEXT || filter?.type === FilterType.NUMBER) {
    return (
      <DebouncedInput
        autoComplete="disabled"
        onClick={e => e.stopPropagation()}
        type={filter.type}
        disabled={filter.disabled}
        className={cn(
          'text-primary w-full rounded border bg-white px-2 py-1 text-sm',
          filterProps?.inputClassName,
          filter.type === FilterType.NUMBER &&
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          filter.disabled && 'cursor-not-allowed opacity-60'
        )}
        value={filter.value[0] ?? ''}
        onChange={value => {
          if (filter.disabled) return;
          const newValue = value.trimStart();
          if (newValue === '') {
            clearFilter?.();
          } else {
            setFilter?.([newValue]);
          }
        }}
      />
    );
  }

  if (filter?.type === FilterType.SELECT) {
    return (
      <SelectFilter
        options={filter.options ?? []}
        value={filter.value}
        disabled={filter.disabled}
        onChange={(vals: string[]) => {
          if (filter.disabled) return;
          if (vals.length === 0) {
            clearFilter?.();
          } else {
            setFilter?.(vals);
          }
        }}
        onSearchTermChange={filterProps?.onSearchTermChange}
        sentryRef={filterProps?.sentryRef}
        hasNextPage={filterProps?.hasNextPage}
        filterProps={filterProps}
      />
    );
  }

  if (filter?.type === FilterType.SINGLE_SELECT) {
    return (
      <SelectFilter
        options={filter.options ?? []}
        value={filter.value}
        disabled={filter.disabled}
        isSingleSelect
        onChange={(vals: string[]) => {
          if (filter.disabled) return;
          if (vals.length === 0) {
            clearFilter?.();
          } else {
            setFilter?.(vals);
          }
        }}
        filterProps={filterProps}
      />
    );
  }

  if (filter?.type === FilterType.DATE) {
    return (
      <DateFilter
        selected={filter.value[1] ? new Date(filter.value[1]) : null}
        disabled={filter.disabled}
        onSelect={date => {
          if (filter.disabled) return;
          if (date) {
            const [start, end] = getDayDateRange(date);

            setFilter?.([
              getBackendTimestamp(start, DATE_TIME_BACKEND_FORMAT_NO_ZONE),
              getBackendTimestamp(end, DATE_TIME_BACKEND_FORMAT_NO_ZONE),
            ]);

            return;
          }
          clearFilter?.();
        }}
      />
    );
  }

  return null;
};

const TableFilters = ({
  filter,
  setFilter,
  clearFilter,
  filterProps,
}: TableFiltersProps) => {
  const filterComponentProps = React.useMemo(
    () => ({
      filter,
      setFilter,
      clearFilter,
      filterProps,
    }),
    [filter, setFilter, clearFilter, filterProps]
  );

  if (!filter?.disabled || !filterProps?.disabledTooltip) {
    return <FilterComponent {...filterComponentProps} />;
  }

  return (
    <Tooltip
      content={filterProps?.disabledTooltip}
      TooltipTriggerProps={{ asChild: true }}
    >
      <span>
        <FilterComponent {...filterComponentProps} />
      </span>
    </Tooltip>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableBadge,
  TableContainer,
};
